// The words, colours and order for CDC's county record, with none of the data.
//
// Kept apart from county-status.ts so a page can label a status without shipping CDC's
// 1,800 FIPS codes to the browser: the year and URL come from their own generated file.

import type { Tone } from "./map/layers";
import {
  COUNTY_TICK_STATUS_SOURCE_URL,
  COUNTY_TICK_STATUS_YEAR,
} from "./geo/county-tick-status-meta";

export type CdcCountyStatus = "established" | "reported" | "no-records";

/** Lowest to highest, the order a key or a scale reads in. */
export const CDC_STATUSES: readonly CdcCountyStatus[] = ["no-records", "reported", "established"];

/** CDC's own word for each status, as it appears in its data. */
export const CDC_STATUS_LABEL: Record<CdcCountyStatus, string> = {
  established: "Established",
  reported: "Reported",
  "no-records": "No records",
};

/** What each status means, in a sentence. "No records" is never "absent". */
export const CDC_STATUS_MEANING: Record<CdcCountyStatus, string> = {
  established:
    "CDC counts a county as established when 6 or more Lone Star ticks, or more than one life stage, are found there in a year.",
  reported:
    "Lone Star ticks have been found here, but fewer than CDC needs to call them established.",
  "no-records":
    "No Lone Star ticks from this county have been reported to CDC. That does not mean there are none.",
};

/**
 * The map colour for each status, borrowed from the site's level scale so a county map
 * reads like the state map: amber for a few records, orange for established, and the
 * grey the map already uses for "no usable data".
 */
export const CDC_STATUS_VAR: Record<CdcCountyStatus, string> = {
  established: "var(--risk-established)",
  reported: "var(--risk-emerging)",
  "no-records": "var(--risk-none)",
};

/**
 * The same colours as fixed hex, for the share card (satori and resvg cannot read CSS
 * variables). Reported and established match riskHex's "Spreading" and "Found here";
 * "No records" is the light grey of the site's no-data colour.
 */
export const CDC_STATUS_HEX: Record<CdcCountyStatus, string> = {
  established: "#dc813b",
  reported: "#e7be62",
  "no-records": "#dde0e4",
};

/** The texture under each colour, so the statuses stay apart without colour. */
export const CDC_STATUS_TONE: Record<CdcCountyStatus, Tone> = {
  established: 2,
  reported: 1,
  "no-records": "none",
};

/** The same sentence with Louisiana's word for a county ("parish") where it applies. */
export function cdcStatusMeaning(status: CdcCountyStatus, noun = "county"): string {
  return CDC_STATUS_MEANING[status].replaceAll("county", noun);
}

export const CDC_STATUS_SOURCE = {
  year: COUNTY_TICK_STATUS_YEAR,
  url: COUNTY_TICK_STATUS_SOURCE_URL,
  sourceId: "cdc-lst-county-data",
} as const;

/**
 * The one-line answer for a county, from CDC's record. "No records" is not a "No": it
 * says what CDC has, and the paragraph under it says why that is not the same thing.
 */
export function countyHeadline(label: string, status: CdcCountyStatus): string {
  switch (status) {
    case "established":
      return `Yes. Lone Star ticks are established in ${label}.`;
    case "reported":
      return `Yes. Lone Star ticks have been found in ${label}.`;
    default:
      return `CDC has no records of Lone Star ticks in ${label}.`;
  }
}

/** A state's counties sorted into CDC's statuses, as compact FIPS lists for a loader. */
export interface StateCountyStatuses {
  established: string[];
  reported: string[];
  /** How many of the state's counties CDC's file lists at all. */
  total: number;
}

/** Read one county's status out of a state's lists, without the national data. */
export function statusIn(lists: StateCountyStatuses, fips: string): CdcCountyStatus {
  if (lists.established.includes(fips)) return "established";
  if (lists.reported.includes(fips)) return "reported";
  return "no-records";
}
